import {
    buildEpisodeDisciplineLink,
    buildEpisodeLink,
} from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/routes';

/**
 * Where a dashboard container's rows link to.
 *
 * What a container counts lives in `@axle-health/work-queue-definitions`. What happens
 * when a scheduler clicks a row stays here, because it is a route and the reporting
 * service that shares those definitions has no use for it.
 */

/**
 * A container's key.
 *
 * The values are declared in the definitions package, which is also what the reporting
 * service reads. They are persisted per variation in `WorkQueueSnooze.container_key`.
 */
export type ContainerDefinitionKey = string;

export const defaultCliniciansRowToLink = (row: { id: string }): string => `/clinicians/${row.id}`;
export const defaultEpisodeOfCareRowToLink = (row: {
    id: string;
    patient: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.id });
export const defaultPatientsRowToLink = (row: { id: string }): string => `/patients/${row.id}`;
export const defaultVisitsRowToLink = (row: { id: string }): string => `/visits/${row.id}`;
export const defaultVisitRequestsRowToLink = (row: {
    id: string;
    visit: { id: string } | null;
}): string => (row.visit ? defaultVisitsRowToLink(row.visit) : `/visit-requests/${row.id}`);
export const defaultEpisodeOrdersRowToLink = ({
    patient: { id: patientId },
    discipline,
    episode,
}: {
    patient: { id: string };
    discipline: { id: string } | null;
    episode: { id: string } | null;
}) => {
    return buildEpisodeDisciplineLink(
        {
            patientId,
            episodeId: episode?.id,
        },
        discipline?.id,
    );
};
export const defaultEpisodeVisitsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });

export const episodeVisitHighlightRowToLink = (row: {
    id: string;
    patient: { id: string };
    episode: { id: string } | null;
}): string =>
    row.episode
        ? `${buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id })}#visit-${
              row.id
          }`
        : defaultVisitsRowToLink(row);

// Highlight variant for variations without episodes (for example COFK).
// Route to the patient's scheduling tab and anchor to the visit.
export const patientVisitHighlightRowToLink = (row: {
    id: string;
    patient: { id: string };
}): string => `/patients/${row.patient.id}/scheduling#visit-${row.id}`;

export const defaultEpisodeVisitRequestsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });
